import { Epic } from 'redux-observable';
import { of, from, timer } from 'rxjs';
import { mergeMap, map, catchError, filter, switchMap, takeWhile } from 'rxjs/operators';
import { RootState } from '../store';
import {
  fetchSmsFilterOptionsRequested,
  fetchSmsFilterOptionsSucceeded,
  fetchSmsFilterOptionsFailed,
  fetchSmsDependentSubChannelsRequested,
  fetchSmsDependentSubChannelsSucceeded,
  fetchSmsDependentSubChannelsFailed,
  fetchSmsDependentLOBsRequested,
  fetchSmsDependentLOBsSucceeded,
  fetchSmsDependentLOBsFailed,
  fetchSmsDependentProductNamesRequested,
  fetchSmsDependentProductNamesSucceeded,
  fetchSmsDependentProductNamesFailed,
  fetchSmsDependentChannelsRequested,
  fetchSmsDependentChannelsSucceeded,
  fetchSmsDependentChannelsFailed,
  smsTriggerPreviewRequested,
  smsTriggerPreviewSucceeded,
  smsTriggerPreviewFailed,
  smsTriggerSendRequested,
  smsTriggerSendSucceeded,
  smsTriggerSendFailed,
  smsTriggerStatusRequested,
  smsTriggerStatusSucceeded,
  smsTriggerStatusFailed,
  fetchPoliciesForSelectionRequested,
  fetchPoliciesForSelectionSucceeded,
  fetchPoliciesForSelectionFailed,
  triggerIndividualSmsRequested
} from '../slices/smsTriggerSlice';
import {
  getSmsFilterOptions,
  getSmsDependentFilterOptions,
  getSmsTriggerPreview,
  sendSmsTrigger,
  getSmsTriggerStatus,
  getPoliciesForSmsSelection,
  triggerIndividualSms
} from '@/services/api';

// Fetch SMS Filter Options Epic
export const fetchSmsFilterOptionsEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    filter((action: any) => action.type === fetchSmsFilterOptionsRequested.type),
    mergeMap(() =>
      from(getSmsFilterOptions()).pipe(
        map((response) => fetchSmsFilterOptionsSucceeded(response)),
        catchError((error) => of(fetchSmsFilterOptionsFailed(error.message || 'Failed to fetch SMS filter options')))
      )
    )
  );

// Fetch SMS Dependent Sub-Channels Epic
// Payload can be a single channel string or an array of channels
export const fetchSmsDependentSubChannelsEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    filter((action: any) => action.type === fetchSmsDependentSubChannelsRequested.type),
    mergeMap((action: any) => {
      const channel = action.payload; // Can be string or string[]
      return from(getSmsDependentFilterOptions(channel)).pipe(
        map((response) => fetchSmsDependentSubChannelsSucceeded(response.sub_channels || [])),
        catchError((error) => of(fetchSmsDependentSubChannelsFailed(error.message || 'Failed to fetch dependent sub-channels')))
      );
    })
  );

// Fetch SMS Dependent LOBs Epic
// Payload can be a single sub_channel string or an array of sub_channels
export const fetchSmsDependentLOBsEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    filter((action: any) => action.type === fetchSmsDependentLOBsRequested.type),
    mergeMap((action: any) => {
      const sub_channel = action.payload; // Can be string or string[]
      return from(getSmsDependentFilterOptions(undefined, sub_channel)).pipe(
        map((response) => fetchSmsDependentLOBsSucceeded(response.lobs || [])),
        catchError((error) => of(fetchSmsDependentLOBsFailed(error.message || 'Failed to fetch dependent LOBs')))
      );
    })
  );

// Fetch SMS Dependent Product Names Epic
// Payload can be a single lob string or an array of lobs
export const fetchSmsDependentProductNamesEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    filter((action: any) => action.type === fetchSmsDependentProductNamesRequested.type),
    mergeMap((action: any) => {
      const lob = action.payload; // Can be string or string[]
      return from(getSmsDependentFilterOptions(undefined, undefined, lob)).pipe(
        map((response) => fetchSmsDependentProductNamesSucceeded(response.product_names || [])),
        catchError((error) => of(fetchSmsDependentProductNamesFailed(error.message || 'Failed to fetch dependent product names')))
      );
    })
  );

// Fetch SMS Dependent Channels Epic
// Payload can be a single product_name string or an array of product_names
export const fetchSmsDependentChannelsEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    filter((action: any) => action.type === fetchSmsDependentChannelsRequested.type),
    mergeMap((action: any) => {
      const product_name = action.payload; // Can be string or string[]
      return from(getSmsDependentFilterOptions(undefined, undefined, undefined, product_name)).pipe(
        map((response) => fetchSmsDependentChannelsSucceeded(response.channels || [])),
        catchError((error) => of(fetchSmsDependentChannelsFailed(error.message || 'Failed to fetch dependent channels')))
      );
    })
  );

// SMS Trigger Preview Epic
export const smsTriggerPreviewEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    filter((action: any) => action.type === smsTriggerPreviewRequested.type),
    mergeMap((action: any) => {
      const filters = action.payload;
      return from(getSmsTriggerPreview(filters)).pipe(
        map((response) => smsTriggerPreviewSucceeded(response)),
        catchError((error) => of(smsTriggerPreviewFailed(error.message || 'Failed to preview SMS trigger')))
      );
    })
  );

// SMS Trigger Send Epic
export const smsTriggerSendEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    filter((action: any) => action.type === smsTriggerSendRequested.type),
    mergeMap((action: any) => {
      const filters = action.payload;
      return from(sendSmsTrigger(filters)).pipe(
        map((response) => smsTriggerSendSucceeded(response)),
        catchError((error) => of(smsTriggerSendFailed(error.message || 'Failed to send SMS trigger')))
      );
    })
  );

// SMS Trigger Status Epic (for manual status checks)
export const smsTriggerStatusEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    filter((action: any) => action.type === smsTriggerStatusRequested.type),
    mergeMap((action: any) => {
      const batchId = action.payload;
      return from(getSmsTriggerStatus(batchId)).pipe(
        map((response) => smsTriggerStatusSucceeded(response)),
        catchError((error) => of(smsTriggerStatusFailed(error.message || 'Failed to get SMS status')))
      );
    })
  );

// SMS Trigger Status Polling Epic (automatic polling after successful send)
export const smsTriggerStatusPollingEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    filter((action: any) => action.type === smsTriggerSendSucceeded.type),
    switchMap((action: any) => {
      const batchId = action.payload.batch_id;
      
      // Start polling every 2 seconds
      return timer(0, 2000).pipe(
        switchMap(() => 
          from(getSmsTriggerStatus(batchId)).pipe(
            map((statusResponse) => smsTriggerStatusSucceeded(statusResponse)),
            catchError((error) => of(smsTriggerStatusFailed(error.message || 'Failed to get SMS status')))
          )
        ),
        takeWhile((action: any) => {
          // Keep polling while status is processing or pending
          if (!action || !action.payload) return true;
          const status = action.payload.status;
          return status === 'processing' || status === 'pending';
        }, true) // Include the last emission
      );
    })
  );

// Fetch Policies for Selection Epic
export const fetchPoliciesForSelectionEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    filter((action: any) => action.type === fetchPoliciesForSelectionRequested.type),
    mergeMap((action: any) => {
      const filters = action.payload;
      return from(getPoliciesForSmsSelection(filters)).pipe(
        map((response) => fetchPoliciesForSelectionSucceeded(response)),
        catchError((error) => of(fetchPoliciesForSelectionFailed(error.message || 'Failed to fetch policies for selection')))
      );
    })
  );

// Trigger Individual SMS Epic
export const triggerIndividualSmsEpic: Epic<any, any, RootState> = (action$, state$) =>
  action$.pipe(
    filter((action: any) => action.type === triggerIndividualSmsRequested.type),
    mergeMap((action: any) => {
      const policyNumbers = action.payload;
      const state = state$.value;
      // Use currentFilters.sms_type (from unified filter form) or fallback to policyFilters
      const smsType = state.smsTrigger.currentFilters.sms_type || 
                      state.smsTrigger.policyFilters.sms_type || 
                      'notice_only';
      return from(triggerIndividualSms(policyNumbers, smsType)).pipe(
        map((response) => smsTriggerSendSucceeded(response)),
        catchError((error) => of(smsTriggerSendFailed(error.message || 'Failed to trigger individual SMS')))
      );
    })
  );

export default [
  fetchSmsFilterOptionsEpic,
  fetchSmsDependentSubChannelsEpic,
  fetchSmsDependentLOBsEpic,
  fetchSmsDependentProductNamesEpic,
  fetchSmsDependentChannelsEpic,
  smsTriggerPreviewEpic,
  smsTriggerSendEpic,
  smsTriggerStatusEpic,
  smsTriggerStatusPollingEpic,
  fetchPoliciesForSelectionEpic,
  triggerIndividualSmsEpic
];

